import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import {
  CasesTableFilters,
  ColorCircularProgress,
  ConfirmationModal,
  Link,
  NoDataPlaceholder,
  ProCaseTableRow,
  Table,
  TableEmptyState,
  TablePagination,
  TableResetFilters,
  Typography,
  ProActivityEntryModal,
  ErrorModal,
} from 'components'
import { connect, useSelector, useDispatch } from 'react-redux'
import { colors, CONSTANTS } from 'constants/index'
import {
  getProCases,
  makeCaseInactive,
  stopCaseBilling,
  resetCasesFilter,
  setCasesSorting,
  resetCasesSorting,
  setTableConfiguration,
  shareCaseWithProviders,
} from 'store/actions/cases'
import {
  navigateToActivitiesListInNewTab,
  navigateToCaseDetails,
  navigateToCreateCase,
  navigateToEditCase,
  navigateToInvoicesListInNewTab,
} from 'store/actions/navigation'
import { getUserConnectionsForFilter } from 'store/actions/connections'
import { isEqual } from 'lodash'
import NoCases from 'static/cases/no_cases.png'
import moment from 'moment-timezone'
import { goToZendesk, closeErrorModal } from 'store/actions/common'
import MakeInactiveForbiddenMsg from 'components/cases/MakeInactiveForbiddenMsg'
import { checkProCases, checkProConnections } from '../../store/actions/cases'
import ChatModal from '../../components/chat/ChatModal'
import CaseInactivationErrorModal from './CaseInactivationErrorModal'
import ShareWithProviderModal from '../../components/cases/ShareWithProviderModal'
import PeopleWithAccessModal from '../../components/cases/PeopleWithAccessModal'

const useStyles = makeStyles({
  activitiesContainer: {
    padding: 30,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  tableHeaderCell: {
    borderBottom: `2px solid ${colors.BASIC.COLOR[10]}`,
  },
  clearFilterContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: 16,
  },
  casesTable: {
    overflow: 'initial',
  },
})

const getColumns = isAccountant => [
  {
    value: 'is_active',
    label: '',
  },
  {
    value: 'name',
    label: isAccountant ? 'Project/File name' : 'Case/Matter name',
    sortable: true,
  },
  {
    value: 'client_name',
    label: 'Client',
    sortable: true,
  },
  {
    value: 'fee_type',
    label: 'Type',
    sortable: true,
  },
  {
    value: 'practice_area__name',
    label: isAccountant ? 'Business topic' : 'Practice area',
    sortable: true,
  },
  {
    value: 'updated_at',
    label: 'Last activity',
    sortable: true,
  },
  {
    value: 'buttons',
    label: '',
    sortable: false,
  },
]

const initialFilter = {
  search: '',
  status: 'active',
  clientId: null,
}

const TooltipText = ({ cases, profileIsApproved, addButtonActive, goToZendesk, isAccountant }) => {
  if (!profileIsApproved) {
    const wordings = cases ? (isAccountant ? 'projects' : 'cases') : 'activities'
    return (
      <span>{`You can not manage ${wordings} until your ${
        isAccountant ? 'registration' : 'license'
      } is approved.`}</span>
    )
  }

  if (!addButtonActive) {
    return <span>{isAccountant ? 'Projects' : 'Cases'} can only be created with clients in your connections list.</span>
  }
}

const currentTimezone = moment.tz.guess()

const ProCasesContainer = state => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.auth.currentUser)
  const feeTypes = CONSTANTS.FEE_TYPES()
  const {
    cases,
    count,
    casesAreLoading,
    tableConfiguration,
    filters,
    sorting,
    profileIsApproved,
    getUserConnectionsForFilter,
    getProCases,
    makeCaseInactive,
    stopCaseBilling,
    setCasesSorting,
    setTableConfiguration,
    navigateToCreateCase,
    navigateToCaseDetails,
    navigateToEditCase,
    navigateToActivitiesListInNewTab,
    navigateToInvoicesListInNewTab,
    resetCasesFilter,
    resetCasesSorting,
    timezone,
    checkProConnections,
    addButtonActive,
    goToZendesk,
    checkProCases,
    userHasCases,
    userCasesAreLoading,
  } = state
  const { limit, tablePage } = tableConfiguration
  const [confirmationModalShow, setConfirmationModalShow] = useState(false)
  const [showStopBillingModal, setShowStopBillingModal] = useState(false)
  const [showCancellationError, setShowCancellationError] = useState(false)
  const [selectedCase, setSelectedCase] = useState('')
  const isInitialFilters = isEqual(initialFilter, filters)
  const isAccountant = currentUser?.subrole === 'accountant'
  const [showActivityModal, setActivityModal] = useState({
    open: false,
    action: null,
    type: null,
    activity: {},
  })
  const [showShareProviderModal, setShowShareProviderModal] = useState(false)
  const [selectedCaseForShare, setSelectedCaseForShare] = useState(null)
  const [showPeopleWithAccessModal, setShowPeopleWithAccessModal] = useState(false)
  const [selectedCaseForViewAccess, setSelectedCaseForViewAccess] = useState(null)
  const [showRemoveProviderConfirmation, setShowRemoveProviderConfirmation] = useState(false)
  const [pendingShareData, setPendingShareData] = useState(null)
  const { title: titleErrorModal, isOpen: isOpenErrorModal } = useSelector(state => state.common.errorModal)

  const onActivityModalClose = () => {
    setActivityModal({
      open: false,
      type: null,
      action: null,
      activity: {},
    })
  }

  useEffect(() => {
    getUserConnectionsForFilter()
    getProCases()
    checkProCases()
    return () => {
      resetCasesFilter()
      setTableConfiguration({
        tablePage: 0,
        offset: 0,
      })
    }
  }, [])

  useEffect(() => {
    checkProConnections()
  }, [])

  const onAddCasePress = () => {
    navigateToCreateCase()
  }

  const onMakeCaseInactiveConfirm = async () => {
    try {
      await makeCaseInactive(selectedCase.uuid, selectedCase.name)
      setConfirmationModalShow(false)
      setShowCancellationError(false)
      getProCases()
    } catch (e) {
      console.error(e)
    }
  }

  const onStopBillingConfirm = async () => {
    try {
      await stopCaseBilling(selectedCase.uuid, selectedCase.name)
      setShowStopBillingModal(false)
      setShowCancellationError(false)
      getProCases()
    } catch (e) {
      console.error(e)
    }
  }

  const getRowActions = rowData => {
    const isSharedWithMe = rowData.is_shared && !rowData.is_owner
    let actions = [
      {
        key: 'seeDetails',
        label: 'see details',
        action: data => {
          navigateToCaseDetails(data.uuid)
        },
      },
    ]

    if (
      rowData.can_edit &&
      ((rowData.status === 'active' && rowData.fee_type !== feeTypes['0'].key) ||
        (rowData.fee_type === feeTypes[4].key && (rowData.status === 'stopped' || rowData.status === 'rejected')))
    ) {
      actions = [
        ...actions,
        {
          key: 'edit',
          label: 'edit',
          action: data => {
            navigateToEditCase(data.uuid)
          },
          hint: profileIsApproved ? (
            ''
          ) : (
            <TooltipText
              cases
              profileIsApproved={profileIsApproved}
              goToZendesk={goToZendesk}
              isAccountant={isAccountant}
            />
          ),
          disabled: !profileIsApproved,
        },
      ]
    }

    if (rowData.is_able_to_create_time_entry && rowData.status === 'active') {
      actions = [
        ...actions,
        {
          key: 'newTimeEntry',
          label: isAccountant ? 'time entry' : 'new time entry',
          action: data => {
            setActivityModal(config => ({
              ...config,
              open: true,
              action: 'create',
              type: 'time',
              activity: { caseId: data.uuid, clientId: data.client },
            }))
          },
          hint: profileIsApproved ? (
            ''
          ) : (
            <TooltipText profileIsApproved={profileIsApproved} goToZendesk={goToZendesk} isAccountant={isAccountant} />
          ),
          disabled: !profileIsApproved,
        },
      ]
    }

    if (rowData.is_able_to_create_time_expense_entry && rowData.status === 'active') {
      actions = [
        ...actions,
        {
          key: 'newExpenseEntry',
          label: isAccountant ? 'expense/charge entry' : 'new expense entry',
          action: data => {
            setActivityModal(config => ({
              ...config,
              open: true,
              action: 'create',
              type: isAccountant ? 'charge' : 'expense',
              activity: { caseId: data.uuid, clientId: data.client },
            }))
          },
          hint: profileIsApproved ? (
            ''
          ) : (
            <TooltipText profileIsApproved={profileIsApproved} goToZendesk={goToZendesk} isAccountant={isAccountant} />
          ),
          disabled: !profileIsApproved,
        },
      ]
    }

    if (rowData.is_able_to_see_activities) {
      actions = [
        ...actions,
        {
          key: 'seeRelatedActivities',
          label: 'see related activities',
          action: data => {
            navigateToActivitiesListInNewTab({ caseId: data.uuid })
          },
        },
      ]
    }

    if (rowData.is_able_to_see_invoices) {
      actions = [
        ...actions,
        {
          key: 'seeRelatedInvoices',
          label: 'see related invoices',
          action: data => {
            navigateToInvoicesListInNewTab({ caseId: data.uuid })
          },
        },
      ]
    }
    if (
      rowData.can_inactivate &&
      (rowData.status === 'active' || (rowData.fee_type === 4 && rowData.status !== 'inactive'))
    ) {
      actions = [
        ...actions,
        {
          key: 'makeInactive',
          label: 'make inactive',
          action: data => {
            setSelectedCase(data)
            if (rowData.can_inactivate && rowData.reason_for_disable_inactivate) {
              setShowCancellationError(true)
            } else if (!rowData.can_inactivate) {
              setShowCancellationError(true)
            } else {
              setConfirmationModalShow(true)
            }
          },
          hint: profileIsApproved ? (
            ''
          ) : (
            <TooltipText
              cases
              profileIsApproved={profileIsApproved}
              goToZendesk={goToZendesk}
              isAccountant={isAccountant}
            />
          ),
          disabled: !profileIsApproved,
        },
      ]
    }
    if (rowData.fee_type === 4 && rowData.status !== 'stopped' && rowData.status !== 'rejected' && !isSharedWithMe) {
      actions = [
        ...actions,
        {
          key: 'stopBilling',
          label: 'stop billing',
          action: data => {
            setSelectedCase(data)
            setShowStopBillingModal(true)
          },
          hint: profileIsApproved ? (
            ''
          ) : (
            <TooltipText
              cases
              profileIsApproved={profileIsApproved}
              goToZendesk={goToZendesk}
              isAccountant={isAccountant}
            />
          ),
          disabled: !profileIsApproved,
        },
      ]
    }

    if (isAccountant && rowData.can_share) {
      actions = [
        ...actions,
        {
          key: 'shareWithProvider',
          label: 'share with another provider',
          action: data => {
            setSelectedCaseForShare(data)
            setShowShareProviderModal(true)
          },
          hint: profileIsApproved ? (
            ''
          ) : (
            <TooltipText
              cases
              profileIsApproved={profileIsApproved}
              goToZendesk={goToZendesk}
              isAccountant={isAccountant}
            />
          ),
          disabled: !profileIsApproved,
        },
      ]
    }

    return actions
  }

  const onSort = (name, isAcs) => {
    setCasesSorting(name, isAcs)
    setTableConfiguration({
      tablePage: 0,
      offset: 0,
    })
    getProCases()
  }

  const onRowsPerPageChange = value => {
    setTableConfiguration({
      tablePage: 0,
      offset: 0,
      limit: value,
    })
    getProCases()
  }

  const onPageChange = value => {
    setTableConfiguration({
      tablePage: value,
      offset: value * limit,
    })
    getProCases()
  }

  const onClearFilters = () => {
    resetCasesFilter()
    resetCasesSorting()
    setTableConfiguration({
      tablePage: 0,
      offset: 0,
    })
    getProCases()
  }

  const onErrorModalClose = () => {
    dispatch(closeErrorModal())
    setConfirmationModalShow(false)
    setShowStopBillingModal(false)
    setShowCancellationError(false)
  }

  return (
    <div className={classes.activitiesContainer}>
      <div className={classes.header}>
        <Typography variant="h2">{isAccountant ? 'Projects' : 'Cases'}</Typography>
        <CasesTableFilters showFilters={userHasCases} isCasesAble={state.isCasesAble} />
      </div>
      {userHasCases && (
        <TableResetFilters
          count={count}
          onClearFilters={onClearFilters}
          showClearFilters={!isInitialFilters}
          containerClass={classes.clearFilterContainer}
        />
      )}
      {casesAreLoading || userCasesAreLoading ? (
        <ColorCircularProgress />
      ) : !userHasCases && !count ? (
        <NoDataPlaceholder
          hideButton
          label={
            isAccountant
              ? 'This page will list all your projects, and allows you to create new projects, and view, edit or make inactive existing projects, as well as create time and charge entries for them.'
              : 'This page will list all your cases/matters, and allows you to create cases, and view, edit or make inactive existing cases and clients, as well as create time and expense entries for them.'
          }
          imageSrc={NoCases}
        />
      ) : !count ? (
        <TableEmptyState />
      ) : (
        <Table
          tableColumns={getColumns(isAccountant)}
          className={classes.casesTable}
          tableHeaderCellClass={{ root: classes.tableHeaderCell }}
          tableData={cases}
          getRowActions={getRowActions}
          onSort={onSort}
          sorting={sorting}
          tableDataRowRenderer={(columns, data) => (
            <ProCaseTableRow
              timezone={timezone}
              columns={columns}
              data={data}
              profileIsApproved={profileIsApproved}
              isAccountant={isAccountant}
              onShareClick={caseData => {
                setSelectedCaseForShare(caseData)
                setShowShareProviderModal(true)
              }}
              onViewSharedClick={caseData => {
                setSelectedCaseForViewAccess(caseData)
                setShowPeopleWithAccessModal(true)
              }}
            />
          )}
          footer={
            <TablePagination
              page={tablePage}
              colSpan={getColumns(isAccountant).length}
              count={count}
              rowsPerPage={limit}
              onRowsPerPageChange={onRowsPerPageChange}
              onPageChange={onPageChange}
            />
          }
          activeRows
        />
      )}
      <ConfirmationModal
        isOpen={confirmationModalShow}
        message={`If you make the ${isAccountant ? 'project' : 'case'} ${
          selectedCase.name
        } inactive, you won't be able to create activities for it and no invoice will be generated. Continue?`}
        onClose={() => setConfirmationModalShow(false)}
        title={`Make ${isAccountant ? 'project' : 'case'} inactive?`}
        onConfirm={onMakeCaseInactiveConfirm}
        confirmationButtonText="yes, make inactive"
        cancelationButtonText="cancel"
      />
      <ConfirmationModal
        isOpen={showStopBillingModal}
        message={`If you stop billing for the ${isAccountant ? 'project' : 'case'} ${
          selectedCase.name
        }, no new invoices will be generated for future activities. Continue?`}
        onClose={() => setShowStopBillingModal(false)}
        title={`Stop billing for ${isAccountant ? 'project' : 'case'}?`}
        onConfirm={onStopBillingConfirm}
        confirmationButtonText="yes, stop billing"
        cancelationButtonText="cancel"
      />
      <ProActivityEntryModal
        open={showActivityModal.open}
        type={showActivityModal.type}
        action={showActivityModal.action}
        activity={showActivityModal.activity}
        handleClose={onActivityModalClose}
      />
      <ErrorModal
        isOpen={isOpenErrorModal}
        onClose={onErrorModalClose}
        title={titleErrorModal}
        message={<MakeInactiveForbiddenMsg />}
        cancelationButtonText="ok"
        variant="primary"
      />
      <CaseInactivationErrorModal
        code={selectedCase.reason_for_disable_inactivate}
        isOpen={showCancellationError}
        onClose={() => setShowCancellationError(false)}
        title={
          selectedCase.can_inactivate
            ? `Make ${isAccountant ? 'project' : 'case'} inactive?`
            : `You can not make the ${isAccountant ? 'project' : 'case'} inactive`
        }
        confirmButtonText={'yes, make inactive'}
        cancellationButtonText={selectedCase.can_inactivate ? 'cancel' : 'close'}
        cancelButtonVariant={selectedCase.can_inactivate ? 'secondary' : 'primary'}
        onConfirm={selectedCase.can_inactivate ? onMakeCaseInactiveConfirm : null}
        isAccountant={isAccountant}
      />
      <ShareWithProviderModal
        isOpen={showShareProviderModal}
        onClose={() => {
          setShowShareProviderModal(false)
          setSelectedCaseForShare(null)
        }}
        caseUuid={selectedCaseForShare?.uuid}
        caseName={selectedCaseForShare?.name}
        clientName={selectedCaseForShare?.client__user__first_name || selectedCaseForShare?.client_name}
        isAccountant={isAccountant}
        onDone={async emails => {
          // Refresh cases list after sharing
          getProCases()
        }}
        onRequestRemoveConfirmation={(providersToAdd, providersToRemove, totalProviderCount, validEmails) => {
          setPendingShareData({
            providersToAdd,
            providersToRemove,
            totalProviderCount,
            validEmails,
            caseData: selectedCaseForShare, // Save case data for later use
          })
          setShowRemoveProviderConfirmation(true)
        }}
      />
      <ConfirmationModal
        isOpen={showRemoveProviderConfirmation}
        onClose={() => {
          setShowRemoveProviderConfirmation(false)
          // Reopen ShareWithProviderModal when canceling
          if (pendingShareData && pendingShareData.caseData) {
            setSelectedCaseForShare(pendingShareData.caseData)
            setShowShareProviderModal(true)
          }
          setPendingShareData(null)
        }}
        onConfirm={async () => {
          if (pendingShareData && pendingShareData.caseData) {
            const caseData = pendingShareData.caseData
            await dispatch(
              shareCaseWithProviders(
                caseData.uuid,
                pendingShareData.providersToAdd,
                pendingShareData.providersToRemove,
                caseData.name,
                caseData.client__user__first_name || caseData.client_name,
                pendingShareData.totalProviderCount
              )
            )
            getProCases()
            setShowRemoveProviderConfirmation(false)
            setPendingShareData(null)
          }
        }}
        title="Are you sure you want to remove provider(s) from the shared list?"
        message="Once removed, the provider(s) will no longer have access to this project."
        confirmationButtonText="REMOVE"
        cancelationButtonText="CANCEL"
      />
      <PeopleWithAccessModal
        isOpen={showPeopleWithAccessModal}
        onClose={() => {
          setShowPeopleWithAccessModal(false)
          setSelectedCaseForViewAccess(null)
        }}
        caseUuid={selectedCaseForViewAccess?.uuid}
      />
      <ChatModal />
    </div>
  )
}

const mapStateToProps = ({ cases, accountProfile, calendar, auth }) => ({
  cases: cases.cases,
  count: cases.casesLength,
  casesAreLoading: cases.casesAreLoading,
  tableConfiguration: cases.tableConfiguration,
  filters: cases.filters,
  sorting: cases.sort,
  profileIsApproved: accountProfile.isProfileApproved,
  isCasesAble: auth.activatedFeature.hasCases,
  timezone: calendar.advanced_calendar.timezone || currentTimezone,
  addButtonActive: cases.addButtonActive,
  userHasCases: cases.userHasCases,
  userCasesAreLoading: cases.userCasesAreLoading,
  currentUser: auth.currentUser,
})

const mapDispatchToProps = dispatch => ({
  getUserConnectionsForFilter: () => dispatch(getUserConnectionsForFilter()),
  getProCases: () => dispatch(getProCases()),
  setTableConfiguration: config => dispatch(setTableConfiguration(config)),
  makeCaseInactive: (id, name) => dispatch(makeCaseInactive(id, name)),
  stopCaseBilling: (id, name) => dispatch(stopCaseBilling(id, name)),
  setCasesSorting: (name, isAcs) => dispatch(setCasesSorting(name, isAcs)),
  navigateToCreateCase: () => dispatch(navigateToCreateCase()),
  navigateToCaseDetails: id => dispatch(navigateToCaseDetails(id)),
  navigateToEditCase: id => dispatch(navigateToEditCase(id)),
  navigateToActivitiesListInNewTab: params => dispatch(navigateToActivitiesListInNewTab(params)),
  navigateToInvoicesListInNewTab: params => dispatch(navigateToInvoicesListInNewTab(params)),
  resetCasesFilter: () => dispatch(resetCasesFilter()),
  resetCasesSorting: () => dispatch(resetCasesSorting()),
  checkProConnections: () => dispatch(checkProConnections()),
  goToZendesk: () => dispatch(goToZendesk()),
  checkProCases: () => dispatch(checkProCases()),
})

export default connect(mapStateToProps, mapDispatchToProps)(ProCasesContainer)
